import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, X, Calendar, Check, ExternalLink, Trash2, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";

const fmtWhen = (iso, tz) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz });

// Family/player-facing: "Book a Zoom Session" button + dialog with day → slot picker and the player's upcoming sessions.
export function BookSessionButton({ playerId, playerName, accent = "#3aa0ff" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-3 py-2 border text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white/5" style={{ color: accent, borderColor: `${accent}55` }} data-testid="book-session-btn">
        <Video className="w-3.5 h-3.5" /> Book a Session
      </button>
      {open && <BookSessionDialog playerId={playerId} playerName={playerName} onClose={() => setOpen(false)} />}
    </>
  );
}

export function BookSessionDialog({ playerId, playerName, onClose }) {
  const [avail, setAvail] = useState(null);
  const [mine, setMine] = useState([]);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [note, setNote] = useState("");
  const [withWhom, setWithWhom] = useState("");
  const [mode, setMode] = useState("zoom");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.sessionAvailability(playerId), api.listSessions({ player_id: playerId, scope: "upcoming" })]);
      setAvail(a); setMine(m);
      setDay(d => d && a.days.some(x => x.date === d) ? d : a.days[0]?.date || null);
    } catch { toast.error("Could not load availability"); setAvail({ days: [] }); }
  };
  useEffect(() => { load(); }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      if (mode === "phone" && phone.replace(/\D/g, "").length < 7) { toast.error("Enter a phone number for the coach to call"); setBusy(false); return; }
      const s = await api.bookSession({ player_id: playerId, start: slot.start, note, with_whom: withWhom, mode, phone: mode === "phone" ? phone : "" });
      setDone(s); setSlot(null); setNote("");
      toast.success("Session booked — confirmation email sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); load(); } finally { setBusy(false); }
  };
  const cancel = async (s) => {
    if (!window.confirm("Cancel this session?")) return;
    try { await api.cancelSession(s.id); toast.success("Session cancelled"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  const tz = avail?.timezone;
  const dayObj = avail?.days.find(d => d.date === day);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto bg-[#141414] border border-white/10 p-0 [&>button]:hidden" data-testid="book-session-dialog">
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-white/10">
          <div>
            <div className="text-[9px] uppercase tracking-[0.35em] text-[#3aa0ff] font-bold flex items-center gap-1.5"><Video className="w-3 h-3" /> {avail?.title || "Virtual Zoom Session"}{avail?.coach_name ? ` · ${avail.coach_name}` : ""}</div>
            <DialogTitle className="font-heading text-2xl uppercase tracking-tight text-white mt-1">Book a session for {playerName}</DialogTitle>
            {avail && <div className="text-[11px] text-[#757575] mt-1">{avail.slot_minutes}-minute sessions · times shown in {tz?.replace("_", " ")} · 1 per day</div>}
          </div>
          <button onClick={onClose} className="p-2 text-[#a0a0a0] hover:text-white" data-testid="book-session-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {done && (
            <div className="border border-emerald-400/40 bg-emerald-400/10 p-4" data-testid="book-session-done">
              <div className="flex items-center gap-2 text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-bold"><Check className="w-3.5 h-3.5" /> Booked</div>
              <div className="text-white text-sm mt-1">{fmtWhen(done.start, tz)}</div>
              {done.mode === "phone" ? <div className="text-[11px] text-amber-300 mt-2 flex items-center gap-1.5" data-testid="book-session-phone-done"><Phone className="w-3 h-3" /> Coach will call {done.phone}</div>
                : <a href={done.join_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-[#3aa0ff] hover:underline" data-testid="book-session-join">Zoom link <ExternalLink className="w-3 h-3" /></a>}
            </div>
          )}
          {!avail ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#3aa0ff]" /></div> : avail.days.length === 0 ? (
            <div className="text-sm italic text-[#757575] py-6 text-center" data-testid="book-session-empty">No open times in the next two weeks — check back soon.</div>
          ) : (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="book-session-days">
                {avail.days.map(d => (
                  <button key={d.date} onClick={() => { setDay(d.date); setSlot(null); }} className={`shrink-0 px-3 py-2 border text-[11px] uppercase tracking-[0.15em] font-bold ${day === d.date ? "bg-[#3aa0ff] border-[#3aa0ff] text-[#0a0a0a]" : "border-white/15 text-[#c0c0c0] hover:border-white/40"}`} data-testid={`book-day-${d.date}`}>
                    {d.label}<span className="block text-[9px] font-normal opacity-70 mt-0.5">{d.slots.length} open</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="book-session-slots">
                {(dayObj?.slots || []).map(s => (
                  <button key={s.start} onClick={() => setSlot(s)} className={`px-2 py-2.5 border text-[12px] font-medium ${slot?.start === s.start ? "border-[var(--org-accent)] text-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/10 text-white hover:border-white/40"}`} data-testid={`book-slot-${s.start}`}>{s.label}</button>
                ))}
              </div>
              {slot && (
                <div className="border border-white/10 bg-[#101010] p-4 space-y-3" data-testid="book-session-confirm">
                  <div className="text-sm text-white"><Calendar className="w-3.5 h-3.5 inline mr-2 text-[#3aa0ff]" />{fmtWhen(slot.start, tz)} · {avail.slot_minutes} min</div>
                  <div className="grid grid-cols-2 gap-2" data-testid="book-session-mode">
                    {[["zoom", "Zoom", Video], ["phone", "Phone Call", Phone]].map(([m, label, Icon]) => (
                      <button key={m} type="button" onClick={() => setMode(m)} className={`flex items-center justify-center gap-2 px-3 py-2.5 border text-[10px] uppercase tracking-[0.25em] font-bold ${mode === m ? "border-[var(--org-accent)] text-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/15 text-[#c0c0c0] hover:border-white/40"}`} data-testid={`book-mode-${m}`}><Icon className="w-3.5 h-3.5" /> {label}</button>
                    ))}
                  </div>
                  {mode === "phone" && <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Best number for the coach to call" className="w-full bg-[#141414] border border-white/15 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#3aa0ff]" data-testid="book-session-phone" />}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5">Who is this meeting with?</div>
                    <div className="grid grid-cols-2 gap-2" data-testid="book-session-with">
                      {[["player", "Player"], ["parent", "Parent"]].map(([v, label]) => (
                        <button key={v} type="button" onClick={() => setWithWhom(v)} className={`px-3 py-2 border text-[10px] uppercase tracking-[0.25em] font-bold ${withWhom === v ? "border-[var(--org-accent)] text-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/15 text-[#c0c0c0] hover:border-white/40"}`} data-testid={`book-with-${v}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5">What do you want to focus on?</div>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Finishing through contact, film from last weekend, recruiting questions…" className="w-full bg-[#141414] border border-white/15 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#3aa0ff]" data-testid="book-session-note" />
                  </div>
                  <button onClick={book} disabled={busy || !withWhom || !note.trim()} className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] font-bold bg-[var(--org-accent)] text-[#121212] hover:bg-[var(--org-accent-hover)] disabled:opacity-50" data-testid="book-session-submit">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : mode === "phone" ? <Phone className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />} Confirm {mode === "phone" ? "phone call" : "Zoom booking"}
                  </button>
                </div>
              )}
            </>
          )}
          {mine.length > 0 && (
            <div data-testid="book-session-mine">
              <div className="text-[9px] uppercase tracking-[0.35em] text-[#757575] font-bold mb-2">Upcoming sessions</div>
              <div className="divide-y divide-white/5 border border-white/10">
                {mine.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm" data-testid={`my-session-${s.id}`}>
                    <div className="text-white">{fmtWhen(s.start, tz)}<span className="text-[#757575] text-[11px] ml-2">{s.slot_minutes} min{s.mode === "phone" ? ` · call ${s.phone}` : ""}</span></div>
                    <div className="flex items-center gap-2">
                      {s.mode === "phone" ? <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-300 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</span> : <a href={s.join_url} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#3aa0ff] hover:underline" data-testid={`my-session-join-${s.id}`}>Join</a>}
                      <button onClick={() => cancel(s)} className="p-1.5 text-[#757575] hover:text-red-400" title="Cancel (24h notice)" data-testid={`my-session-cancel-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
